import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getHostelAdmissions, addHostelAdmission,
  updateHostelAdmission, deleteHostelAdmission,
  addHostelPayment, getHostelAdmission,
  checkoutHostelAdmission,
  getHostelAdmissionOptions,
  generateCertificatesFromResidents,
  getAttendanceHistory, getMealsHistory, getHostelPayments, getInvoices, getInvoice,
  getBillingAdjustments, addBillingAdjustment, deleteBillingAdjustment,
  lookupByCnic, getResidentHistoryByCnic,
} from '../api';
import { useNavigate } from 'react-router-dom';
import { FiCreditCard, FiEdit2, FiPrinter, FiUpload, FiX, FiCheckSquare, FiCoffee, FiUser } from 'react-icons/fi';
import ResidentProfileModal from './ResidentProfileModal';
import { usePermissions } from '../utils/permissions';

const MAX_DOB = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 10); return d.toISOString().split('T')[0]; })();

function validateAdmission(form) {
  const name = (form.full_name || '').trim();
  if (!name || name.length < 2) return 'Full name is required (letters only, min 2 characters)';
  if (!/^[a-zA-Z\s'.-]+$/.test(name)) return 'Full name must contain letters only';
  const cnic = (form.cnic || '').replace(/-/g, '');
  if (cnic && !/^\d{13}$/.test(cnic)) return 'CNIC must be exactly 13 digits (e.g. 3520112345671)';
  const phone = (form.contact_no || '').replace(/[\s-]/g, '');
  if (phone && !/^03\d{9}$/.test(phone)) return 'Contact must be Pakistani format: 03XXXXXXXXX (11 digits)';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Invalid email address';
  if (form.dob && form.dob > MAX_DOB) return 'Resident must be at least 10 years old';
  if (!form.date_of_admission) return 'Date of admission is required';
  if (!form.monthly_rent || Number(form.monthly_rent) <= 0) return 'Monthly rent must be a positive number';
  return null;
}

const emptyForm = {
  hostel_id: '', full_name: '', father_name: '', gender: '', dob: '',
  cnic: '', dept_batch_program: '', campus_id: '', course_id: '', course_level: '', contact_no: '', email: '', permanent_address: '',
  guardian_name: '', guardian_relationship: '', guardian_contact: '',
  date_of_admission: '', date_of_exit: '', monthly_rent: '',
  student_signature: '', incharge_signature: '', notes: '', status: 'active'
};

// ── Signature Component ────────────────────────────────────────────────
function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState(value ? 'preview' : 'draw');

  const startPos = useRef({});

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onStart = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    setDrawing(true);
    startPos.current = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(startPos.current.x, startPos.current.y);
  };

  const onMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const onEnd = () => {
    setDrawing(false);
    if (canvasRef.current) onChange(canvasRef.current.toDataURL());
  };

  const clearCanvas = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { onChange(ev.target.result); setMode('preview'); };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
        <label style={{ color: '#374151', fontSize: '13px', fontWeight: '600' }}>{label}</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} onClick={() => setMode('draw')}><FiEdit2 /> Draw</button>
          <label className="btn btn-secondary btn-small" style={{ fontSize: '11px', cursor: 'pointer', margin: 0 }}>
            <FiUpload /> Upload <input type="file" accept="image/*" hidden onChange={handleUpload} />
          </label>
          {value && <button type="button" className="btn btn-danger btn-small" style={{ fontSize: '11px' }} onClick={() => { clearCanvas(); onChange(''); setMode('draw'); }}>Clear</button>}
        </div>
      </div>

      {mode === 'draw' ? (
        <canvas
          ref={canvasRef}
          width={280} height={80}
          style={{ border: '1px dashed #d1d5db', borderRadius: '4px', background: '#f9fafb', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        />
      ) : value ? (
        <div onClick={() => setMode('draw')} style={{ cursor: 'pointer' }}>
          <img src={value} alt="Signature" style={{ maxHeight: '80px', maxWidth: '100%', border: '1px dashed #4b5563', borderRadius: '4px' }} />
        </div>
      ) : (
        <canvas ref={canvasRef} width={280} height={80} style={{ border: '1px dashed #d1d5db', borderRadius: '4px', background: '#f9fafb', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        />
      )}
      {/* Device sign placeholder */}
      {/* TODO: Add physical device signature pad integration here */}
    </div>
  );
}
// Format ISO date strings from MySQL
const fmtDate = v => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-PK', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
// ── Print View ─────────────────────────────────────────────────────────
function PrintView({ record, onClose }) {
  if (!record) return null;

  const handlePrint = () => {
    const el = document.getElementById('print-area');
    if (!el) return;
    const clone = el.cloneNode(true);
    const inlineStyle = clone.querySelector('style');
    if (inlineStyle) inlineStyle.remove();
    const w = window.open('', '_blank');
    w.document.write('<!DOCTYPE html><html><head><title>Admission Form</title><style>body{margin:20px;font-family:Arial,sans-serif;}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important;}</style></head><body>');
    w.document.write(clone.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); w.close(); }, 400);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#1a1a1a', borderRadius: '12px', width: '100%', maxWidth: '700px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #333', background: '#111' }}>
          <h3 style={{ color: '#fff', margin: 0 }}>Admission Form</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handlePrint} style={{ padding: '6px 14px', background: '#1e4a8a', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '13px' }}><FiPrinter /> Print</button>
            <button onClick={onClose} style={{ padding: '6px 14px', background: '#374151', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '13px' }}><FiX /></button>
          </div>
        </div>
        <div style={{ padding: '20px' }} id="print-area">
          <div style={{ border: '2px solid #1d4ed8', borderRadius: '8px', overflow: 'hidden', background: '#fff', color: '#111' }}>
            <div style={{ background: '#0c2340', padding: '20px 24px 16px', textAlign: 'center', borderBottom: '3px solid #1d4ed8' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '8px' }}>
                <img src="/assets/hit-logo.png" alt="HIT" style={{ height: '54px', width: 'auto', filter: 'brightness(1.2)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '17px', fontWeight: '800', color: '#ffffff', letterSpacing: '0.8px' }}>HAZZA INSTITUTE OF TECHNOLOGY</div>
                  <div style={{ fontSize: '11px', color: '#93c5fd', marginTop: '2px' }}>Hostel Management System</div>
                </div>
              </div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#60a5fa', letterSpacing: '1px', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px' }}>HOSTEL ADMISSION FORM</div>
              <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>Hostel: <strong style={{ color: '#fff' }}>{record.hostel_name}</strong></div>
            </div>
            <div style={{ padding: '20px 24px' }}>
            {[
              ['Full Name', record.full_name], ["Father's Name", record.father_name], ['Gender', record.gender],
              ['Date of Birth', fmtDate(record.dob)], ['CNIC', record.cnic], ['Campus', record.campus_name], ['Course', record.course_name], ['Course Level', record.course_level_name ? `Level ${record.course_level_number} - ${record.course_level_name}` : '—'],
              ['Contact No.', record.contact_no], ['Email', record.email], ['Permanent Address', record.permanent_address],
              ['Guardian Name', record.guardian_name], ['Relationship', record.guardian_relationship], ['Emergency Contact', record.guardian_contact],
              ['Date of Admission', fmtDate(record.date_of_admission)], ['Date of Exit', fmtDate(record.date_of_exit)],
              ['Monthly Rent', record.monthly_rent ? `Rs. ${Number(record.monthly_rent).toLocaleString()}` : '—'],
              ['Status', record.status],
              ['Notes', record.notes]
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', borderBottom: '1px solid #e5e7eb', padding: '5px 0', fontSize: '12px' }}>
                <strong style={{ color: '#374151' }}>{l}</strong>
                <span>{v || '—'}</span>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
              <div style={{ textAlign: 'center' }}>
                {record.student_signature ? <img src={record.student_signature} alt="sig" style={{ maxHeight: '55px', border: '1px solid #d1d5db', marginBottom: '6px' }} /> : <div style={{ height: '55px', borderBottom: '1px solid #000', marginBottom: '6px' }}></div>}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>Student Signature</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                {record.incharge_signature ? <img src={record.incharge_signature} alt="sig" style={{ maxHeight: '55px', border: '1px solid #d1d5db', marginBottom: '6px' }} /> : <div style={{ height: '55px', borderBottom: '1px solid #000', marginBottom: '6px' }}></div>}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>In-charge Signature</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#374151' }}>Date: ____________________________</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Payment Modal ───────────────────────────────────────────────────────
function PaymentModal({ admission, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], months_covered: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.amount || !form.payment_date) { setError('Amount and date are required'); return; }
    setSaving(true);
    try {
      await addHostelPayment({ ...form, admission_id: admission.id, hostel_id: admission.hostel_id });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to record payment');
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="modal-header">
          <h3><FiCreditCard /> Record Payment</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: '12px' }}>
          {error && <div className="alert alert-error">{error}</div>}
          <p style={{ color: '#555', margin: 0 }}>Resident: <strong>{admission.full_name}</strong></p>
          <p style={{ color: '#555', margin: 0 }}>
            Balance Due: <strong style={{ color: admission.balance > 0 ? '#ef4444' : '#0e1e45' }}>
              Rs. {Number(admission.balance || 0).toLocaleString()}
            </strong>
          </p>
          <div className="form-group">
            <label>Amount (Rs.) *</label>
            <input className="form-control" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="5000" />
          </div>
          <div className="form-group">
            <label>Payment Date *</label>
            <input className="form-control" type="date" value={form.payment_date} onChange={e => setForm(p => ({ ...p, payment_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Months Covered</label>
            <input className="form-control" type="number" value={form.months_covered} onChange={e => setForm(p => ({ ...p, months_covered: e.target.value }))} placeholder="e.g. 2" />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input className="form-control" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Record Payment'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────
export default function HostelAdmissions() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [admissions, setAdmissions] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [printRecord, setPrintRecord] = useState(null);
  const [profileRecord, setProfileRecord] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [checkoutTarget, setCheckoutTarget] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState('');
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [generatingCertificates, setGeneratingCertificates] = useState(false);
  const [filters, setFilters] = useState({ hostel_id: '', status: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.hostel_id) params.hostel_id = filters.hostel_id;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const [adRes, optRes] = await Promise.all([getHostelAdmissions(params), getHostelAdmissionOptions()]);
      setAdmissions(adRes.data);
      setHostels(optRes.data.hostels || []);
      setCampuses(optRes.data.campuses || []);
      setCourses(optRes.data.courses || []);
      setLevels(optRes.data.levels || []);
      setSelectedIds([]);
    } catch (e) {
      setError('Failed to load admissions');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setError(''); setCnicLookupNote(''); setShowModal(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, date_of_exit: a.date_of_exit || '', password: '' }); setError(''); setCnicLookupNote(''); setShowModal(true); };

  const openPrint = async (id) => {
    try {
      const res = await getHostelAdmission(id);
      setPrintRecord(res.data);
    } catch (e) { alert('Failed to load record'); }
  };

  const handleSave = async () => {
    if (!form.hostel_id) { setError('Please select a hostel'); return; }
    if (!form.campus_id || !form.course_id || !form.course_level) {
      setError('Campus, course, and course level are required');
      return;
    }
    const validErr = validateAdmission(form);
    if (validErr) { setError(validErr); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await updateHostelAdmission(editing.id, form);
      } else {
        try {
          await addHostelAdmission(form);
        } catch (e) {
          if (e.response?.data?.warning && window.confirm(e.response.data.error + '\n\nClick OK to admit them here anyway, or Cancel to stop.')) {
            await addHostelAdmission({ ...form, force: true });
          } else {
            throw e;
          }
        }
      }
      setShowModal(false); load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save admission');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteHostelAdmission(id);
      setDeleteConfirm(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  };

  const handleCheckout = async () => {
    if (!checkoutDate) { alert('Please select check-out date'); return; }
    setCheckoutSaving(true);
    try {
      await checkoutHostelAdmission(checkoutTarget.id, { date_of_exit: checkoutDate });
      setCheckoutTarget(null); setCheckoutDate('');
      load();
    } catch (e) { alert(e.response?.data?.error || 'Checkout failed'); }
    setCheckoutSaving(false);
  };

  const handleGenerateCertificates = async () => {
    if (!selectedIds.length) return;
    setGeneratingCertificates(true);
    try {
      const res = await generateCertificatesFromResidents(selectedIds);
      alert(`Done. Generated: ${res.data.generatedCount}, Skipped: ${res.data.skippedCount}`);
      setSelectedIds([]);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to generate certificates');
    }
    setGeneratingCertificates(false);
  };

  const totalDues = admissions.reduce((s, a) => s + (a.total_dues || 0), 0);
  const totalPaid = admissions.reduce((s, a) => s + (a.total_paid || 0), 0);
  const totalBalance = totalDues - totalPaid;

  const setF = (key, val) => setForm((prev) => {
    const next = { ...prev, [key]: val };
    if (key === 'course_id') {
      next.dept_batch_program = courseNameById(val) || prev.dept_batch_program;
    }
    return next;
  });

  const courseNameById = (id) => {
    const hit = courses.find(c => String(c.id) === String(id));
    return hit ? hit.name : '';
  };

  // On Add (never on Edit), leaving the CNIC field auto-fills identity
  // fields from an existing record for the same person elsewhere in the
  // system - avoids re-typing (and mis-typing) a name/DOB/contact that's
  // already on file for this exact CNIC.
  const [cnicLookupNote, setCnicLookupNote] = useState('');
  const handleCnicBlur = async () => {
    if (editing) return;
    const cnic = (form.cnic || '').trim();
    if (cnic.length < 13) { setCnicLookupNote(''); return; }
    try {
      const res = await lookupByCnic(cnic);
      if (res.data.found) {
        const d = res.data.data;
        setForm(prev => ({
          ...prev,
          full_name: prev.full_name || d.full_name || '',
          father_name: prev.father_name || d.father_name || '',
          gender: prev.gender || d.gender || '',
          dob: prev.dob || (d.dob ? String(d.dob).slice(0, 10) : ''),
          contact_no: prev.contact_no || d.contact_no || '',
          email: prev.email || d.email || '',
          permanent_address: prev.permanent_address || d.permanent_address || '',
          guardian_name: prev.guardian_name || d.guardian_name || '',
          guardian_relationship: prev.guardian_relationship || d.guardian_relationship || '',
          guardian_contact: prev.guardian_contact || d.guardian_contact || '',
        }));
        setCnicLookupNote(`Found an existing record for this CNIC (${d.full_name}) - filled in below, edit anything that's changed.`);
      } else {
        setCnicLookupNote('');
      }
    } catch (e) { /* non-fatal */ }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Hostel Admissions</h2>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Resident records, financial tracking</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedIds.length > 0 && can('students', 'create') && (
            <button className="btn btn-success" onClick={handleGenerateCertificates} disabled={generatingCertificates}>
              {generatingCertificates ? 'Generating...' : `Generate Certificates (${selectedIds.length})`}
            </button>
          )}
          {can('hostel_admissions', 'create') && <button className="btn btn-primary" onClick={openAdd}>Add Resident</button>}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', margin: '20px 0' }}>
        {[
          { label: 'Total Residents', value: admissions.length, color: '#3b82f6' },
          { label: 'Total Dues', value: `Rs. ${totalDues.toLocaleString()}`, color: '#f59e0b' },
          { label: 'Total Paid to Hostel', value: `Rs. ${totalPaid.toLocaleString()}`, color: '#0e1e45' },
          { label: 'Total Balance', value: `Rs. ${totalBalance.toLocaleString()}`, color: totalBalance > 0 ? '#ef4444' : '#0e1e45' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', border: `1px solid ${c.color}44`, borderRadius: '10px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input className="form-control" style={{ flex: '1', minWidth: '180px', maxWidth: '280px' }}
          placeholder="Search name, CNIC, contact..."
          value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} />
        <select className="form-control" style={{ minWidth: '160px' }} value={filters.hostel_id} onChange={e => setFilters(p => ({ ...p, hostel_id: e.target.value }))}>
          <option value="">All Hostels</option>
          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <select className="form-control" style={{ minWidth: '130px' }} value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="exited">Exited</option>
        </select>
        {(filters.search || filters.hostel_id || filters.status) &&
          <button className="btn btn-secondary" onClick={() => setFilters({ hostel_id: '', status: '', search: '' })}>Clear</button>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={admissions.length > 0 && selectedIds.length === admissions.length}
                    onChange={(e) => setSelectedIds(e.target.checked ? admissions.map(a => a.id) : [])}
                  />
                </th>
                <th>#</th>
                <th>Name</th>
                <th>Hostel</th>
                <th>Contact</th>
                <th>Admission Date</th>
                <th>Rent/mo</th>
                <th>Dues</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Added / Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admissions.length === 0 ? (
                <tr><td colSpan="13" style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>No admissions found.</td></tr>
              ) : admissions.map((a, i) => (
                <tr key={a.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(a.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds((prev) => [...new Set([...prev, a.id])]);
                        else setSelectedIds((prev) => prev.filter(id => id !== a.id));
                      }}
                    />
                  </td>
                  <td>{i + 1}</td>
                  <td><strong style={{ color: '#1f2937' }}>{a.full_name}</strong><br /><small style={{ color: '#6b7280' }}>{a.cnic || ''}</small></td>
                  <td style={{ color: '#0e1e45' }}>{a.hostel_name}</td>
                  <td>{a.contact_no || '—'}</td>
                  <td>{a.date_of_admission ? new Date(a.date_of_admission).toLocaleDateString('en-PK') : '—'}</td>
                  <td>Rs. {Number(a.monthly_rent || 0).toLocaleString()}</td>
                  <td style={{ color: '#f59e0b' }}>Rs. {Number(a.total_dues || 0).toLocaleString()}</td>
                  <td style={{ color: '#0e1e45' }}>Rs. {Number(a.total_paid || 0).toLocaleString()}</td>
                  <td style={{ color: (a.balance || 0) > 0 ? '#ef4444' : '#0e1e45', fontWeight: '600' }}>
                    Rs. {Number(a.balance || 0).toLocaleString()}
                  </td>
                  <td>
                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: a.status === 'active' ? 'rgba(4,120,87,0.15)' : 'rgba(107,114,128,0.15)', color: a.status === 'active' ? '#0e1e45' : '#6b7280' }}>
                      {a.status}
                    </span>
                  </td>
                  <td style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                    <div>Added: {a.created_at ? new Date(a.created_at).toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</div>
                    {a.updated_at && a.updated_at !== a.created_at && <div style={{ color: '#d97706' }}>Updated: {new Date(a.updated_at).toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' })}</div>}
                  </td>
                  <td>
                    <div className="action-buttons" style={{ gap: '4px' }}>
                      <button className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} title="Complete profile" onClick={() => setProfileRecord(a)}><FiUser /> Profile</button>
                      <button className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} onClick={() => openPrint(a.id)}>View</button>
                      {can('hostel_finances', 'create') && <button className="btn btn-primary btn-small" style={{ fontSize: '11px' }} onClick={() => setPaymentTarget(a)}>Pay</button>}
                      {can('hostel_admissions', 'edit') && <button className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} onClick={() => openEdit(a)}>Edit</button>}
                      {can('hostel_attendance', 'view') && <button className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} title="Attendance history" onClick={() => navigate(`/hostel-attendance?hostel_id=${a.hostel_id}&admission_id=${a.id}`)}><FiCheckSquare /></button>}
                      {can('hostel_meals', 'view') && <button className="btn btn-secondary btn-small" style={{ fontSize: '11px' }} title="Meal history" onClick={() => navigate(`/hostel-meals?hostel_id=${a.hostel_id}&admission_id=${a.id}`)}><FiCoffee /></button>}
                      {a.status === 'active' && can('hostel_admissions', 'edit') && (
                        <button className="btn btn-small" style={{ fontSize: '11px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', color: '#d97706' }} onClick={() => { setCheckoutTarget(a); setCheckoutDate(new Date().toISOString().split('T')[0]); }}>Checkout</button>
                      )}
                      {can('hostel_admissions', 'delete') && <button className="btn btn-danger btn-small" style={{ fontSize: '11px' }} onClick={() => setDeleteConfirm(a.id)}>Del</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Admission' : 'New Hostel Admission'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px', maxHeight: '70vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-error">{error}</div>}

              <h4 style={{ color: '#0e1e45', margin: '0 0 -6px', borderBottom: '1px solid #d1fae5', paddingBottom: '6px' }}>Personal Information</h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Hostel *</label>
                  <select className="form-control" value={form.hostel_id} onChange={e => setF('hostel_id', e.target.value)}>
                    <option value="">Select Hostel</option>
                    {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input className="form-control" value={form.full_name} onChange={e => setF('full_name', e.target.value)} placeholder="Enter resident full name" />
                </div>
                <div className="form-group">
                  <label>Father's Name</label>
                  <input className="form-control" value={form.father_name} onChange={e => setF('father_name', e.target.value)} placeholder="Enter father's full name" />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select className="form-control" value={form.gender} onChange={e => setF('gender', e.target.value)} style={{ color: form.gender ? '' : '#9ca3af' }}>
                    <option value="">-- Select Gender --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input className="form-control" type="date" value={form.dob} max={MAX_DOB} onChange={e => setF('dob', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>CNIC</label>
                  <input className="form-control" value={form.cnic} onChange={e => setF('cnic', e.target.value)} onBlur={handleCnicBlur} placeholder="13 digits e.g. 3520112345671" maxLength={13} />
                  {cnicLookupNote && <small style={{ color: 'var(--color-success-dark, #0a9d5c)' }}>{cnicLookupNote}</small>}
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Course *</label>
                  <select className="form-control" value={form.course_id} onChange={e => setF('course_id', e.target.value)}>
                    <option value="">Select Course</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Contact No.</label>
                  <input className="form-control" value={form.contact_no} onChange={e => setF('contact_no', e.target.value)} placeholder="Enter mobile number e.g. 03001234567" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => setF('email', e.target.value)} placeholder="Enter email address" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Permanent Address</label>
                  <textarea className="form-control" rows="2" value={form.permanent_address} onChange={e => setF('permanent_address', e.target.value)} placeholder="Enter full permanent address" />
                </div>
              </div>

              <h4 style={{ color: '#0e1e45', margin: '4px 0 -6px', borderBottom: '1px solid #d1fae5', paddingBottom: '6px' }}>Guardian / Emergency Contact</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Guardian Name</label>
                  <input className="form-control" value={form.guardian_name} onChange={e => setF('guardian_name', e.target.value)} placeholder="Enter guardian full name" />
                </div>
                <div className="form-group">
                  <label>Relationship</label>
                  <input className="form-control" value={form.guardian_relationship} onChange={e => setF('guardian_relationship', e.target.value)} placeholder="e.g. Father, Brother, Uncle" />
                </div>
                <div className="form-group">
                  <label>Emergency Contact</label>
                  <input className="form-control" value={form.guardian_contact} onChange={e => setF('guardian_contact', e.target.value)} placeholder="Enter guardian mobile number" />
                </div>
              </div>

              <h4 style={{ color: '#0e1e45', margin: '4px 0 -6px', borderBottom: '1px solid #d1fae5', paddingBottom: '6px' }}>Hostel Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Campus *</label>
                  <select className="form-control" value={form.campus_id} onChange={e => setF('campus_id', e.target.value)}>
                    <option value="">Select Campus</option>
                    {campuses.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Course Level *</label>
                  <select className="form-control" value={form.course_level} onChange={e => setF('course_level', e.target.value)}>
                    <option value="">Select Level</option>
                    {levels.map(l => <option key={l.id} value={l.id}>Level {l.level} - {l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Admission *</label>
                  <input className="form-control" type="date" value={form.date_of_admission} onChange={e => setF('date_of_admission', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Monthly Rent (Rs.) *</label>
                  <input className="form-control" type="number" value={form.monthly_rent} onChange={e => setF('monthly_rent', e.target.value)} placeholder="Enter monthly rent amount" />
                </div>
                {editing && (
                  <>
                    <div className="form-group">
                      <label>Date of Exit</label>
                      <input className="form-control" type="date" value={form.date_of_exit} onChange={e => setF('date_of_exit', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-control" value={form.status} onChange={e => setF('status', e.target.value)}>
                        <option value="active">Active</option>
                        <option value="exited">Exited</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea className="form-control" rows="2" value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Any additional notes or remarks" />
                </div>
              </div>

              <h4 style={{ color: '#0e1e45', margin: '4px 0 -6px', borderBottom: '1px solid #d1fae5', paddingBottom: '6px' }}>Signatures</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <SignaturePad label="Student Signature" value={form.student_signature} onChange={v => setF('student_signature', v)} />
                <SignaturePad label="Hostel In-charge Signature" value={form.incharge_signature} onChange={v => setF('incharge_signature', v)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Record' : 'Add Resident')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {printRecord && <PrintView record={printRecord} onClose={() => setPrintRecord(null)} />}
      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(admissionId) => getAttendanceHistory({ admission_id: admissionId })}
          fetchMeals={(admissionId) => getMealsHistory({ admission_id: admissionId })}
          fetchPayments={(admissionId) => getHostelPayments({ admission_id: admissionId })}
          fetchInvoices={(admissionId) => getInvoices({ admission_id: admissionId })}
          fetchInvoiceDetail={(id) => getInvoice(id)}
          fetchAdjustments={(admissionId) => getBillingAdjustments({ admission_id: admissionId })}
          fetchResidentHistory={(cnic) => getResidentHistoryByCnic(cnic)}
          canManageAdjustments
          onAddAdjustment={addBillingAdjustment}
          onDeleteAdjustment={deleteBillingAdjustment}
        />
      )}

      {/* Payment Modal */}
      {paymentTarget && (
        <PaymentModal
          admission={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onSaved={() => { setPaymentTarget(null); load(); }}
        />
      )}

      {/* Checkout Modal */}
      {checkoutTarget && (
        <div className="modal-overlay" onClick={() => setCheckoutTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Checkout Resident</h3>
              <button className="modal-close" onClick={() => setCheckoutTarget(null)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              <p style={{ color: '#555', margin: 0 }}>Checking out: <strong>{checkoutTarget.full_name}</strong></p>
              <div className="form-group">
                <label>Date of Exit *</label>
                <input className="form-control" type="date" value={checkoutDate} onChange={e => setCheckoutDate(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCheckoutTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCheckout} disabled={checkoutSaving}>
                {checkoutSaving ? 'Processing...' : 'Confirm Checkout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Confirm Delete</h3></div>
            <div className="modal-body"><p style={{ color: '#555' }}>Delete this admission record? This cannot be undone.</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
